/**
 * MyTrainingPage — Personal training status and compliance dashboard.
 *
 * Thin constellation instance viewer for `constellation-staff-training`,
 * centered on the logged-in user. Backend-driven per Dumb Frontend (DRR-055).
 *
 * Legislation backing (MN ALF):
 * - MN 144G.45 Subd 3: 8 hrs/year annual training, 6 mandatory topics
 * - MN 144G.72: Dementia-specific training for memory care
 * - MN 626.557: Mandatory reporter training
 * - OSHA 1910.1030: Bloodborne pathogen training
 *
 * Data sources:
 * - Tasks with task_group_ref matching training task groups (tg-4-9-001, tg-0-2-001)
 * - Constellation instance for staff-training (Ring 2: competencies, Ring 3: certificates)
 * - rl-4-0-043 enforcement: 30-day warning, shift block if overdue
 *
 * Route: /ferp-core/my-training
 * Scope: training:self:read (from role-0-0-1 base employee role)
 */

import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Badge,
  Card,
  Container,
  Group,
  Loader,
  Progress,
  SimpleGrid,
  Stack,
  Tabs,
  Text,
  Title,
  ThemeIcon,
  Center,
  UnstyledButton,
} from '@mantine/core';
import {
  IconCertificate,
  IconBook,
  IconCalendarDue,
  IconHistory,
  IconAlertTriangle,
  IconCheck,
} from '@tabler/icons-react';
import { useMyActor } from '@/hooks/useMyActor';
import {
  useGetTasksByActorQuery,
  GqlTaskStatus,
} from '@/generated/graphql';

export function MyTrainingPage() {
  const navigate = useNavigate();
  const { actorId, loading: actorLoading } = useMyActor();

  // Fetch tasks assigned to current user, then filter to training-related task groups
  const { data: taskData, loading: tasksLoading } = useGetTasksByActorQuery({
    variables: { actorId: actorId ?? '' },
    skip: !actorId,
  });

  const loading = actorLoading || tasksLoading;

  // Filter tasks to training task groups
  // Per config-type-registry.yaml filename_pattern, group_id can be:
  //   - New format: "{subgraph}-{category}-{seq}" (e.g., "0-2-1", "4-13-1")
  //   - Legacy format: "tg-{subgraph}-{category}-{seq}" (e.g., "tg-4-9-1")
  // Match both patterns for training-related task groups.
  const trainingTasks = useMemo(() => {
    const tasks = taskData?.getTasksByActor ?? [];
    return tasks.filter((t) => {
      const ref = t.taskGroupRef;
      if (!ref) return false;
      // Strip optional "tg-" prefix for consistent matching
      const normalized = ref.startsWith('tg-') ? ref.slice(3) : ref;
      return (
        normalized.startsWith('0-2-') ||   // Universal training (core)
        normalized.startsWith('4-9-') ||   // MN ALF annual training
        normalized.startsWith('4-13-') ||  // Orientation + dementia competencies
        normalized.startsWith('5-4-')      // Certification renewal
      );
    });
  }, [taskData]);

  const completedCount = trainingTasks.filter(
    (t) => t.status === GqlTaskStatus.COMPLETED
  ).length;
  const pendingCount = trainingTasks.filter(
    (t) => t.status !== GqlTaskStatus.COMPLETED && t.status !== GqlTaskStatus.CANCELLED
  ).length;
  const totalCount = trainingTasks.length;
  const progressPct = totalCount > 0 ? Math.round((completedCount / totalCount) * 100) : 0;

  if (loading) {
    return (
      <Center h="60vh">
        <Loader size="lg" />
      </Center>
    );
  }

  return (
    <Container size="xl" py="md">
      <Stack gap="lg">
        <Group justify="space-between">
          <Group gap="xs">
            <ThemeIcon size="lg" variant="light" color="grape">
              <IconCertificate size={20} />
            </ThemeIcon>
            <Title order={2}>My Training</Title>
          </Group>
          {pendingCount > 0 && (
            <Badge color="orange" size="lg" variant="light">
              {pendingCount} pending
            </Badge>
          )}
        </Group>

        {/* Status cards */}
        <SimpleGrid cols={{ base: 1, sm: 3 }}>
          <Card withBorder padding="lg">
            <Group justify="space-between" mb="xs">
              <Text size="sm" c="dimmed">Completed</Text>
              <ThemeIcon size="sm" variant="light" color="green">
                <IconCheck size={14} />
              </ThemeIcon>
            </Group>
            <Text size="xl" fw={700}>{completedCount}</Text>
          </Card>
          <Card withBorder padding="lg">
            <Group justify="space-between" mb="xs">
              <Text size="sm" c="dimmed">Pending</Text>
              <ThemeIcon size="sm" variant="light" color="orange">
                <IconCalendarDue size={14} />
              </ThemeIcon>
            </Group>
            <Text size="xl" fw={700}>{pendingCount}</Text>
          </Card>
          <Card withBorder padding="lg">
            <Group justify="space-between" mb="xs">
              <Text size="sm" c="dimmed">Progress</Text>
              <ThemeIcon size="sm" variant="light" color="blue">
                <IconBook size={14} />
              </ThemeIcon>
            </Group>
            <Progress value={progressPct} size="lg" color="grape" mt="xs" />
            <Text size="xs" c="dimmed" ta="right" mt={4}>{progressPct}%</Text>
          </Card>
        </SimpleGrid>

        {/* Tab content */}
        <Tabs defaultValue="required">
          <Tabs.List>
            <Tabs.Tab value="required" leftSection={<IconBook size={14} />}>
              Required Training
            </Tabs.Tab>
            <Tabs.Tab value="certificates" leftSection={<IconCertificate size={14} />}>
              Certificates
            </Tabs.Tab>
            <Tabs.Tab value="history" leftSection={<IconHistory size={14} />}>
              History
            </Tabs.Tab>
          </Tabs.List>

          <Tabs.Panel value="required" pt="md">
            {pendingCount === 0 ? (
              <Alert color="green" title="All training complete" icon={<IconCheck size={16} />}>
                You have no outstanding training requirements.
              </Alert>
            ) : (
              <Stack gap="sm">
                {trainingTasks
                  .filter((t) => t.status !== GqlTaskStatus.COMPLETED && t.status !== GqlTaskStatus.CANCELLED)
                  .map((task) => (
                    <UnstyledButton
                      key={task.id}
                      onClick={() => navigate(`/ferp-core/tasks/${task.id}`)}
                      style={{ width: '100%' }}
                    >
                      <Card withBorder padding="sm" style={{ cursor: 'pointer' }}>
                        <Group justify="space-between">
                          <Stack gap={2}>
                            <Text fw={500}>{task.name || 'Training Task'}</Text>
                            <Text size="xs" c="dimmed">
                              {task.taskGroupRef ?? 'No group'}
                            </Text>
                          </Stack>
                          <Badge
                            color={task.status === GqlTaskStatus.ASSIGNED ? 'blue' : 'orange'}
                            variant="light"
                          >
                            {task.status}
                          </Badge>
                        </Group>
                      </Card>
                    </UnstyledButton>
                  ))}
              </Stack>
            )}
          </Tabs.Panel>

          <Tabs.Panel value="certificates" pt="md">
            <Alert color="blue" title="Certificates" icon={<IconCertificate size={16} />}>
              Certificate tracking will display your uploaded credentials, CPR certs,
              and license expiration dates from your training constellation (Ring 3).
            </Alert>
          </Tabs.Panel>

          <Tabs.Panel value="history" pt="md">
            {completedCount === 0 ? (
              <Alert color="gray" title="No training history" icon={<IconHistory size={16} />}>
                Completed training records will appear here.
              </Alert>
            ) : (
              <Stack gap="sm">
                {trainingTasks
                  .filter((t) => t.status === GqlTaskStatus.COMPLETED)
                  .map((task) => (
                    <UnstyledButton
                      key={task.id}
                      onClick={() => navigate(`/ferp-core/tasks/${task.id}`)}
                      style={{ width: '100%' }}
                    >
                      <Card withBorder padding="sm" style={{ cursor: 'pointer' }}>
                        <Group justify="space-between">
                          <Stack gap={2}>
                            <Text fw={500}>{task.name || 'Training Task'}</Text>
                            <Text size="xs" c="dimmed">
                              {task.taskGroupRef ?? 'No group'}
                            </Text>
                          </Stack>
                          <Badge color="green" variant="light">
                            Completed
                          </Badge>
                        </Group>
                      </Card>
                    </UnstyledButton>
                  ))}
              </Stack>
            )}
          </Tabs.Panel>
        </Tabs>
      </Stack>
    </Container>
  );
}
